/**
 * Product buy box: option selection, variant resolution, price and gallery.
 *
 * Deliberately not a web component. It has no state worth sharing, no reason to
 * re-render on a Section Rendering fetch, and the whole page is server-rendered
 * -- so a single module that reads the variant table Liquid already emitted is
 * both smaller and easier to reason about than a Component subclass.
 *
 * Combinations that do not exist are marked unavailable rather than hidden.
 * Hiding them makes the control jump as the shopper explores, and hides the
 * fact that a combination was ever offered.
 */

const root = document.querySelector('[data-pdp]');

if (root) {
  const variants = JSON.parse(root.dataset.variants || '[]');
  const optionGroups = [...root.querySelectorAll('[data-pdp-option]')];
  const stage = root.querySelector('[data-pdp-stage]');
  const gallery = root.querySelector('product-gallery');
  const idField = root.querySelector('[data-pdp-variant-id]');
  const priceEl = root.querySelector('[data-pdp-price]');
  const compareEl = root.querySelector('[data-pdp-compare]');
  const savingEl = root.querySelector('[data-pdp-saving]');
  const submit = root.querySelector('[data-pdp-submit]');
  const submitText = root.querySelector('[data-pdp-submit-text]');
  const subPriceEl = root.querySelector('[data-pdp-sub-price]');
  const onePriceEl = root.querySelector('[data-pdp-one-price]');

  const moneyFormat = document.documentElement.dataset.moneyFormat || '${{amount}}';

  /** Shopify prices are integer cents. */
  const money = (cents) =>
    moneyFormat.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2)).replace(/\{\{.*?\}\}/g, '');

  const selected = () => optionGroups.map((g) => g.querySelector('[data-selected="true"]')?.dataset.pdpValue ?? null);

  const findVariant = (choice) =>
    variants.find((v) => choice.every((val, i) => val === null || v.options[i] === val));

  /** A combination is offered if any variant matches it, available or not. */
  const exists = (index, value) => {
    const choice = selected();
    choice[index] = value;
    return variants.some((v) => choice.every((val, i) => val === null || v.options[i] === val));
  };

  function markUnavailable() {
    optionGroups.forEach((group, i) => {
      group.querySelectorAll('[data-pdp-value]').forEach((btn) => {
        const offered = exists(i, btn.dataset.pdpValue);
        btn.dataset.unavailable = String(!offered);
        btn.setAttribute('aria-disabled', String(!offered));
      });
    });
  }

  function subscriptionPlanId() {
    const subCard = root.querySelector('[data-pdp-plan="sub"]');
    if (!subCard || subCard.dataset.selected !== 'true') return null;
    const chip = subCard.querySelector('[data-pdp-plan-id][data-selected="true"]');
    return chip ? chip.dataset.pdpPlanId : (subCard.querySelector('input')?.value ?? null);
  }

  function render() {
    const variant = findVariant(selected());
    markUnavailable();

    if (!variant) {
      submit.disabled = true;
      submitText.textContent = submit.dataset.unavailableText || 'Unavailable';
      return;
    }

    idField.value = variant.id;

    // Selling-plan pricing comes from the allocation, not from a discount we
    // compute here -- the merchant may change the plan's discount at any time.
    const planId = subscriptionPlanId();
    const allocation = planId
      ? variant.selling_plan_allocations?.find((a) => String(a.selling_plan_id) === String(planId))
      : null;
    const effective = allocation ? allocation.price : variant.price;
    const reference = variant.compare_at_price && variant.compare_at_price > effective ? variant.compare_at_price : null;

    priceEl.textContent = money(effective);

    if (compareEl) {
      compareEl.hidden = !reference;
      if (reference) compareEl.textContent = money(reference);
    }
    if (savingEl) {
      const saved = reference ? reference - effective : 0;
      savingEl.textContent = saved > 0 ? `You save ${money(saved)}` : '';
    }

    if (subPriceEl && variant.selling_plan_allocations?.length) {
      const first = planId
        ? variant.selling_plan_allocations.find((a) => String(a.selling_plan_id) === String(planId))
        : variant.selling_plan_allocations[0];
      subPriceEl.textContent = money(first ? first.price : variant.price);
    }
    if (onePriceEl) onePriceEl.textContent = money(variant.price);

    submit.disabled = !variant.available;
    submitText.textContent = variant.available
      ? submit.dataset.addText || submitText.textContent
      : submit.dataset.soldOutText || 'Sold out';

    // Ask the gallery to scroll to this variant's media rather than mutating an
    // <img> it owns. The media is already on the page, so there is nothing to
    // load and the thumbnails stay in sync for free.
    if (variant.featured_media?.id && gallery?.showMedia) {
      gallery.showMedia(variant.featured_media.id);
    }

    // Tint the gallery ground with the selected swatch so the page takes on the
    // flavor's color instead of staying one fixed brand tone.
    const swatchBtn = root.querySelector('[data-pdp-option] [data-selected="true"] > span[style*="background"]');
    if (stage && swatchBtn) {
      stage.style.setProperty('--pdp-stage-tint', swatchBtn.style.background);
    }

    root.dispatchEvent(new CustomEvent('pdp:variant', { bubbles: true, detail: { variant, planId } }));
  }

  function selectWithin(scope, el) {
    scope.querySelectorAll('[data-selected]').forEach((n) => {
      n.dataset.selected = 'false';
      if (n.hasAttribute('aria-pressed')) n.setAttribute('aria-pressed', 'false');
    });
    el.dataset.selected = 'true';
    if (el.hasAttribute('aria-pressed')) el.setAttribute('aria-pressed', 'true');
  }

  optionGroups.forEach((group) => {
    group.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-pdp-value]');
      if (!btn) return;
      selectWithin(group, btn);
      render();
    });
  });

  root.querySelectorAll('[data-pdp-plan]').forEach((card) => {
    card.addEventListener('click', () => {
      root.querySelectorAll('[data-pdp-plan]').forEach((c) => (c.dataset.selected = 'false'));
      card.dataset.selected = 'true';
      card.querySelector('input[type="radio"]').checked = true;
      render();
    });
  });

  root.querySelectorAll('[data-pdp-plan-id]').forEach((chip) => {
    chip.addEventListener('click', (event) => {
      event.stopPropagation();
      event.preventDefault();
      const scope = chip.parentElement;
      scope.querySelectorAll('[data-pdp-plan-id]').forEach((c) => (c.dataset.selected = 'false'));
      chip.dataset.selected = 'true';
      const subCard = chip.closest('[data-pdp-plan="sub"]');
      subCard.querySelector('input[type="radio"]').value = chip.dataset.pdpPlanId;
      subCard.querySelector('input[type="radio"]').checked = true;
      root.querySelectorAll('[data-pdp-plan]').forEach((c) => (c.dataset.selected = 'false'));
      subCard.dataset.selected = 'true';
      render();
    });
  });

  /* ------------------------------------------------------------ quantity --- */

  // Minimum is 1 here, unlike the cart where 0 is how a line is removed.
  root.querySelectorAll('[data-pdp-qty-up], [data-pdp-qty-down]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const input = root.querySelector('[data-pdp-qty-input]');
      if (!input) return;
      const step = btn.hasAttribute('data-pdp-qty-up') ? 1 : -1;
      input.value = String(Math.max(1, (Number(input.value) || 1) + step));
    });
  });

  root.querySelector('[data-pdp-qty-input]')?.addEventListener('change', (event) => {
    const n = Math.max(1, Math.floor(Number(event.target.value) || 1));
    event.target.value = String(n);
  });

  render();
}
